import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Tooltip, Stack, CircularProgress } from "@mui/material";
import axios from "src/utils/axios";
import Label from "src/components/label";
import Iconify from "src/components/iconify";
import { useAuthContext } from "src/auth/useAuthContext";
import { getRealTimeOrdersCount } from "src/redux/slices/ordersSlice";

export default function UnsyncedTaxInvoicesButton() {
  const dispatch = useDispatch();
  const { user } = useAuthContext();
  const { orderCount } = useSelector((state) => state.ordersSlice);

  const [processingCount, setProcessingCount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const unsyncedCount = Number(orderCount?.unsyncedCount || 0);

  const {
    isPOSAvailable,
    isPraIntegrationAvailable,
    isFbrIntegrationAvailable,
  } = user?.venue?.configurations ?? {};

  const venueId = user?.venue?.id || user?.venue?._id || user?.venueId;

  const hasActiveTaxAuthorityFeature =
    isPraIntegrationAvailable || isFbrIntegrationAvailable;

  const authorityLabel = isPraIntegrationAvailable
    ? "PRA"
    : isFbrIntegrationAvailable
      ? "FBR"
      : "Tax Authority";

  useEffect(() => {
    if (unsyncedCount > 0 && !isProcessing) {
      setIsCompleted(false);
    }
  }, [unsyncedCount, isProcessing]);

  const handleProcessInvoices = async () => {
    if (unsyncedCount <= 0 || isProcessing || isCompleted) return;

    const token = sessionStorage.getItem("accessToken");

    try {
      setIsProcessing(true);
      setIsCompleted(false);
      setProcessingCount(unsyncedCount);

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/order/syncUnsyncedTaxInvoicesForPOS`,
        {
          venueId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setProcessingCount(0);
      setIsCompleted(true);

      await dispatch(getRealTimeOrdersCount(venueId, token));
    } catch (error) {
      console.error("Tax invoices sync failed:", error);

      setIsCompleted(false);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isPOSAvailable || !hasActiveTaxAuthorityFeature) {
    return null;
  }

  const getTooltipTitle = () => {
    if (isProcessing) {
      return `Processing ${processingCount} ${authorityLabel} invoices`;
    }

    if (isCompleted) {
      return `${authorityLabel} invoices synced successfully`;
    }

    return `Unsynced ${authorityLabel} invoices: ${unsyncedCount}`;
  };

  const renderIcon = () => {
    if (isProcessing) {
      return (
        <CircularProgress
          size={16}
          thickness={5}
          sx={{
            mr: 0.7,
            color: "inherit",
          }}
        />
      );
    }

    if (isCompleted) {
      return (
        <Iconify
          icon="mdi:receipt-text-check-outline"
          sx={{
            width: 18,
            height: 18,
            mr: 0.5,
          }}
        />
      );
    }

    return (
      <Iconify
        icon="mdi:receipt-clock-outline"
        sx={{
          width: 18,
          height: 18,
          mr: 0.5,
        }}
      />
    );
  };

  return (
    <Tooltip title={getTooltipTitle()}>
      <span>
        <Button
          size="small"
          variant={isProcessing || isCompleted ? "contained" : "outlined"}
          color={isProcessing ? "warning" : isCompleted ? "success" : "primary"}
          onClick={handleProcessInvoices}
          disabled={unsyncedCount <= 0 || isProcessing || isCompleted}
          sx={{
            minWidth: 52,
            height: 32,
            px: 1,
            borderRadius: 1,
            fontWeight: 700,
            whiteSpace: "nowrap",
            textTransform: "none",
          }}
        >
          {renderIcon()}

          <Stack direction="row" alignItems="center" spacing={0.5}>
            {!isProcessing && !isCompleted && (
              <Label
                color={unsyncedCount > 0 ? "error" : "default"}
                sx={{
                  minWidth: 20,
                  height: 20,
                  px: 0.6,
                }}
              >
                {unsyncedCount}
              </Label>
            )}

            {isProcessing && (
              <Label
                color="warning"
                sx={{
                  minWidth: 20,
                  height: 20,
                  px: 0.6,
                }}
              >
                {processingCount}
              </Label>
            )}

            {isCompleted && (
              <Label
                color="success"
                sx={{
                  minWidth: 20,
                  height: 20,
                  px: 0.6,
                }}
              >
                0
              </Label>
            )}
          </Stack>
        </Button>
      </span>
    </Tooltip>
  );
}